import React, { useEffect, useMemo, useState } from 'react';
import MaterialReactTable from 'material-react-table';
import { MRT_Localization_PT } from 'material-react-table/locales/pt';
import { IconButton } from '@mui/material';
import { Delete, ExpandLess, ExpandMore, Visibility } from '@material-ui/icons';
import {
  Button,
  Card,
  CardBody,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';
import Select from '../../../components/Template/Input/Select';
import currencyFormatter from '../../../utils/currencyFormatter';
import {
  adicionarAtividade,
  adicionarMacroatividade,
  atualizarAtividade,
  atualizarMacroatividade,
  atualizarSetorVinculo,
  chaveItemOrcamento,
  custoDaAtividade,
  custoDaMacro,
  definirVinculoAtividade,
  excluirAtividade,
  excluirMacroatividade,
  incluirAtividadesNaMacro,
  itemEhRecursoHumano,
  quantidadeInteiraPositiva,
  removerVinculoAtividade,
  rotuloItemOrcamento,
  setoresDoCargo,
  valorUnitarioItem,
} from './atividadesOrcamento';

const propsTabela = {
  localization: MRT_Localization_PT,
  enableColumnResizing: true,
  enableGrouping: false,
  enableGlobalFilter: true,
  enableFilters: true,
  enableDensityToggle: false,
  enableColumnFilters: false,
  initialState: {
    density: 'compact',
    pagination: { pageIndex: 0, pageSize: 10 },
  },
  muiTableBodyCellProps: { sx: { fontSize: '13px' } },
  muiTableHeadCellProps: {
    sx: { fontSize: '12px', fontWeight: 'normal', padding: '8px' },
  },
  muiTopToolbarProps: {
    sx: {
      '.MuiBox-root': { marginTop: '10px', height: '20px', display: 'flex', alignItems: 'center' },
    },
  },
};

function CampoDia({ value, onChange, ariaLabel }) {
  return (
    <Input
      type="number"
      min="0"
      step="1"
      bsSize="sm"
      aria-label={ariaLabel}
      value={value ?? ''}
      onChange={(event) => {
        const texto = event.target.value;
        if (texto === '') {
          onChange(null);
          return;
        }
        if (!/^\d+$/.test(texto)) return;
        onChange(Number(texto));
      }}
      style={{ width: 72 }}
    />
  );
}

function linhasAtividade(atividades, estado, propItens, macros) {
  const nomeMacro = new Map(macros.map((macro) => [macro.rowKey, macro.macroatividade_nome || 'Sem nome']));
  return atividades.map((atividade) => ({
    ...atividade,
    custo: custoDaAtividade(estado, atividade.rowKey, propItens),
    macroNome: atividade.macroRowKey ? (nomeMacro.get(atividade.macroRowKey) || 'Sem nome') : 'Sem macroatividade',
  }));
}

function colunasAtividade({ comMacro, onAlterar, onAbrir, onExcluir }) {
  const colunas = [
    {
      accessorKey: 'atividade_nome',
      header: 'Nome',
      Cell: ({ row }) => (
        <Input
          bsSize="sm"
          value={row.original.atividade_nome}
          placeholder="Nome da atividade"
          onChange={(event) => onAlterar((estado) => atualizarAtividade(estado, row.original.rowKey, {
            atividade_nome: event.target.value,
          }))}
        />
      ),
    },
    {
      accessorKey: 'custo',
      header: 'Custo',
      Cell: ({ row }) => currencyFormatter.format(row.original.custo || 0),
    },
    {
      accessorKey: 'inicioPrevDias',
      header: 'Início (dias após a assinatura)',
      Cell: ({ row }) => (
        <CampoDia
          ariaLabel="Dia de início da atividade"
          value={row.original.inicioPrevDias}
          onChange={(inicioPrevDias) => onAlterar((estado) => atualizarAtividade(estado, row.original.rowKey, { inicioPrevDias }))}
        />
      ),
    },
    {
      accessorKey: 'diasParaExecutar',
      header: 'Duração (dias)',
      Cell: ({ row }) => (
        <CampoDia
          ariaLabel="Dias de duração da atividade"
          value={row.original.diasParaExecutar}
          onChange={(diasParaExecutar) => onAlterar((estado) => atualizarAtividade(estado, row.original.rowKey, { diasParaExecutar }))}
        />
      ),
    },
  ];
  if (comMacro) {
    colunas.push({
      accessorKey: 'macroNome',
      header: 'Macroatividade',
    });
  }
  colunas.push({
    id: 'acoes',
    header: 'Ações',
    enableSorting: false,
    Cell: ({ row }) => (
      <span className="d-inline-flex">
        <IconButton size="small" aria-label="Ver atividade" onClick={() => onAbrir(row.original)}>
          <Visibility fontSize="small" />
        </IconButton>
        {onExcluir && (
          <IconButton size="small" aria-label="Excluir atividade" onClick={() => onExcluir(row.original.rowKey)}>
            <Delete fontSize="small" />
          </IconButton>
        )}
      </span>
    ),
  });
  return colunas;
}

function TabelaAtividades({ data, comMacro, onAlterar, onAbrir, onExcluir, rotuloPagina }) {
  const columns = useMemo(
    () => colunasAtividade({ comMacro, onAlterar, onAbrir, onExcluir }),
    [comMacro, onAlterar, onAbrir, onExcluir]
  );
  return (
    <MaterialReactTable
      {...propsTabela}
      columns={columns}
      data={data}
      muiTablePaginationProps={{
        labelRowsPerPage: <span style={{ fontSize: '12px' }}>{rotuloPagina}</span>,
        rowsPerPageOptions: [10, 50, 100],
      }}
    />
  );
}

function ModalDetalheAtividade({ atividade, estado, propItens, setores, cargoSetorAtiv, onFechar, onAlterar }) {
  const [selecionados, setSelecionados] = useState([]);
  const [quantidadesNovas, setQuantidadesNovas] = useState({});
  const [aviso, setAviso] = useState('');

  const itensAlocados = useMemo(() => {
    if (!atividade) return [];
    return (estado.vinculosAtividadeItem || [])
      .filter((vinculo) => vinculo.atividadeRowKey === atividade.rowKey && Number(vinculo.qtd) > 0)
      .map((vinculo) => {
        const item = (propItens || []).find((candidato) => chaveItemOrcamento(candidato) === vinculo.propItemRowKey);
        const unitario = valorUnitarioItem(item);
        return {
          ...vinculo,
          item,
          descricao: rotuloItemOrcamento(item),
          unitario,
          total: unitario * (Number(vinculo.qtd) || 0),
        };
      });
  }, [atividade, estado.vinculosAtividadeItem, propItens]);

  const opcoesParaIncluir = useMemo(() => {
    if (!atividade) return [];
    const jaAlocados = new Set(itensAlocados.map((linha) => linha.propItemRowKey));
    return (propItens || [])
      .filter((item) => !jaAlocados.has(chaveItemOrcamento(item)))
      .map((item) => ({ value: chaveItemOrcamento(item), label: rotuloItemOrcamento(item), item }));
  }, [atividade, estado, propItens, itensAlocados]);

  const colunas = useMemo(() => ([
    { accessorKey: 'descricao', header: 'Descrição' },
    {
      accessorKey: 'qtd',
      header: 'Quantidade',
      Cell: ({ row }) => (
        <Input
          bsSize="sm"
          type="number"
          min="1"
          step="1"
          style={{ width: 100 }}
          defaultValue={row.original.qtd}
          key={`${row.original.propItemRowKey}-${row.original.qtd}`}
          onBlur={(event) => {
            const quantidade = quantidadeInteiraPositiva(event.target.value);
            if (!quantidade || quantidade === Number(row.original.qtd)) return;
            onAlterar((atual) => definirVinculoAtividade(
              atual,
              row.original.atividadeRowKey,
              row.original.propItemRowKey,
              quantidade,
              row.original.setorAtiv_id
            ));
          }}
        />
      ),
    },
    {
      accessorKey: 'unitario',
      header: 'Valor unitário',
      Cell: ({ row }) => currencyFormatter.format(row.original.unitario || 0),
    },
    {
      accessorKey: 'total',
      header: 'Valor total',
      Cell: ({ row }) => currencyFormatter.format(row.original.total || 0),
    },
    {
      id: 'setor',
      header: 'Setor da atividade',
      Cell: ({ row }) => {
        const item = row.original.item;
        if (!itemEhRecursoHumano(item)) return null;
        const opcoesSetor = setoresDoCargo(cargoSetorAtiv, item?.cargo_id).map((linha) => {
          const setor = (setores || []).find((opcao) => Number(opcao.setorAtiv_id) === Number(linha.setorAtiv_id));
          return {
            value: Number(linha.setorAtiv_id),
            label: setor?.setorAtiv_nome || `Setor ${linha.setorAtiv_id}`,
          };
        });
        return (
          <Input
            bsSize="sm"
            type="select"
            disabled={!item?.cargo_id || !opcoesSetor.length}
            value={row.original.setorAtiv_id || ''}
            onChange={(event) => onAlterar((atual) => atualizarSetorVinculo(
              atual,
              row.original.atividadeRowKey,
              row.original.propItemRowKey,
              event.target.value
            ))}
          >
            <option value="">{item?.cargo_id ? 'Setor da atividade' : 'Item sem cargo'}</option>
            {opcoesSetor.map((opcao) => (
              <option key={opcao.value} value={opcao.value}>{opcao.label}</option>
            ))}
          </Input>
        );
      },
    },
    {
      id: 'acoes',
      header: 'Ações',
      Cell: ({ row }) => (
        <Button
          size="sm"
          color="link"
          onClick={() => onAlterar((atual) => removerVinculoAtividade(
            atual,
            row.original.atividadeRowKey,
            row.original.propItemRowKey
          ))}
        >
          Remover
        </Button>
      ),
    },
  ]), [cargoSetorAtiv, onAlterar, setores]);

  if (!atividade) return null;

  const incluir = () => {
    const semQuantidade = selecionados.some((opcao) => !quantidadeInteiraPositiva(quantidadesNovas[opcao.value]));
    if (!selecionados.length || semQuantidade) {
      setAviso('Escolha os itens e informe uma quantidade inteira maior que zero para cada um.');
      return;
    }
    onAlterar((atual) => selecionados.reduce((estadoAtual, opcao) => definirVinculoAtividade(
      estadoAtual,
      atividade.rowKey,
      opcao.value,
      quantidadesNovas[opcao.value],
      null
    ), atual));
    setSelecionados([]);
    setQuantidadesNovas({});
    setAviso('');
  };

  return (
    <Modal isOpen toggle={onFechar} size="xl">
      <ModalHeader toggle={onFechar}>
        {atividade.atividade_nome || 'Atividade sem nome'}
      </ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <h6>Incluir itens</h6>
          <Select
            classNamePrefix="select"
            isMulti
            isClearable
            isSearchable
            placeholder={opcoesParaIncluir.length ? 'Itens orçamentários' : 'Nenhum item disponível'}
            options={opcoesParaIncluir}
            value={selecionados}
            onChange={(valor) => {
              setSelecionados(valor || []);
              setAviso('');
            }}
          />
          {selecionados.map((opcao) => (
            <label key={opcao.value} className="d-flex align-items-center mt-2 mb-0" style={{ gap: 8 }}>
              <span style={{ flex: 1 }}>{opcao.label}</span>
              <Input
                bsSize="sm"
                type="number"
                min="1"
                step="1"
                style={{ width: 100 }}
                placeholder="Qtd"
                value={quantidadesNovas[opcao.value] ?? ''}
                onChange={(event) => setQuantidadesNovas((atual) => ({
                  ...atual,
                  [opcao.value]: event.target.value,
                }))}
              />
            </label>
          ))}
          {aviso && <p className="text-danger mt-2 mb-0">{aviso}</p>}
          <div className="d-flex justify-content-end mt-2">
            <Button size="sm" color="primary" onClick={incluir} disabled={!opcoesParaIncluir.length}>
              Incluir
            </Button>
          </div>
        </div>
        <MaterialReactTable
          {...propsTabela}
          columns={colunas}
          data={itensAlocados}
          muiTablePaginationProps={{
            labelRowsPerPage: <span style={{ fontSize: '12px' }}>Itens por página</span>,
            rowsPerPageOptions: [10, 50, 100],
          }}
        />
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onFechar}>Fechar</Button>
      </ModalFooter>
    </Modal>
  );
}

function ModalIncluirAtividades({ macro, estado, propItens, onFechar, onAlterar }) {
  const [selecionadas, setSelecionadas] = useState([]);
  const [quantidades, setQuantidades] = useState({});

  useEffect(() => {
    if (!macro) return;
    setSelecionadas([]);
    const atuais = {};
    (propItens || []).forEach((item) => {
      const chave = chaveItemOrcamento(item);
      const vinculo = (estado.vinculosMacroItem || []).find((itemVinculo) => (
        itemVinculo.macroRowKey === macro.rowKey && itemVinculo.propItemRowKey === chave
      ));
      atuais[chave] = vinculo ? String(vinculo.qtd) : '';
    });
    setQuantidades(atuais);
  }, [macro, propItens, estado.vinculosMacroItem]);

  if (!macro) return null;
  const disponiveis = (estado.atividades || []).filter((atividade) => atividade.macroRowKey !== macro.rowKey);
  const alternar = (rowKey) => {
    setSelecionadas((atual) => (
      atual.includes(rowKey) ? atual.filter((chave) => chave !== rowKey) : [...atual, rowKey]
    ));
  };

  return (
    <Modal isOpen toggle={onFechar} size="lg">
      <ModalHeader toggle={onFechar}>
        Adicionar atividades em {macro.macroatividade_nome || 'macroatividade sem nome'}
      </ModalHeader>
      <ModalBody>
        <h6>Atividades da proposta</h6>
        {!disponiveis.length && <p>Todas as atividades da proposta já estão nesta macroatividade.</p>}
        {disponiveis.map((atividade) => (
          <label key={atividade.rowKey} className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
            <Input
              type="checkbox"
              checked={selecionadas.includes(atividade.rowKey)}
              onChange={() => alternar(atividade.rowKey)}
            />
            <span>{atividade.atividade_nome || 'Atividade sem nome'}</span>
          </label>
        ))}
        <h6 className="mt-3">Quantidade de cada item nesta macroatividade</h6>
        {(propItens || []).map((item) => {
          const chave = chaveItemOrcamento(item);
          return (
            <div key={chave} className="d-flex align-items-center mb-2" style={{ gap: 12 }}>
              <span style={{ flex: 1 }}>{rotuloItemOrcamento(item)}</span>
              <span>{currencyFormatter.format(valorUnitarioItem(item))}</span>
              <Input
                bsSize="sm"
                type="number"
                min="1"
                step="1"
                style={{ width: 100 }}
                value={quantidades[chave] ?? ''}
                onChange={(event) => setQuantidades((atual) => ({ ...atual, [chave]: event.target.value }))}
              />
            </div>
          );
        })}
        {!propItens?.length && <p className="mb-0">A proposta ainda não tem itens orçamentários.</p>}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onFechar}>Cancelar</Button>
        <Button
          color="primary"
          onClick={() => {
            const quantidadesValidas = {};
            Object.entries(quantidades).forEach(([chave, valor]) => {
              quantidadesValidas[chave] = quantidadeInteiraPositiva(valor) || '';
            });
            onAlterar((atual) => incluirAtividadesNaMacro(atual, macro.rowKey, selecionadas, quantidadesValidas));
            onFechar();
          }}
        >
          Incluir
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function CardMacroatividade({ macro, estado, propItens, onAlterar, onAbrirAtividade }) {
  const [aberto, setAberto] = useState(false);
  const [aba, setAba] = useState('atividades');
  const [modalAberto, setModalAberto] = useState(false);
  const fim = macro.inicioPrevDias != null && macro.diasParaExecutar != null
    ? macro.inicioPrevDias + macro.diasParaExecutar
    : null;
  const atividades = (estado.atividades || []).filter((atividade) => atividade.macroRowKey === macro.rowKey);
  const itens = (estado.vinculosMacroItem || [])
    .filter((vinculo) => vinculo.macroRowKey === macro.rowKey)
    .map((vinculo) => {
      const item = (propItens || []).find((candidato) => chaveItemOrcamento(candidato) === vinculo.propItemRowKey);
      const unitario = valorUnitarioItem(item);
      return {
        ...vinculo,
        descricao: rotuloItemOrcamento(item),
        unitario,
        total: unitario * (Number(vinculo.qtd) || 0),
      };
    });
  const colunasItem = useMemo(() => ([
    { accessorKey: 'descricao', header: 'Descrição' },
    { accessorKey: 'qtd', header: 'Quantidade' },
    {
      accessorKey: 'unitario',
      header: 'Valor unitário',
      Cell: ({ row }) => currencyFormatter.format(row.original.unitario || 0),
    },
    {
      accessorKey: 'total',
      header: 'Valor total',
      Cell: ({ row }) => currencyFormatter.format(row.original.total || 0),
    },
    { id: 'acoes', header: 'Ações', Cell: () => null },
  ]), []);

  return (
    <Card className="mb-3">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 12 }}>
          <Input
            value={macro.macroatividade_nome}
            placeholder="Nome da macroatividade"
            onChange={(event) => onAlterar((atual) => atualizarMacroatividade(atual, macro.rowKey, {
              macroatividade_nome: event.target.value,
            }))}
            style={{ maxWidth: 360 }}
          />
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
            <label className="mb-0 d-flex align-items-center" style={{ gap: 6 }}>
              Início dia
              <CampoDia
                ariaLabel="Dia de início da macroatividade"
                value={macro.inicioPrevDias}
                onChange={(inicioPrevDias) => onAlterar((atual) => atualizarMacroatividade(atual, macro.rowKey, { inicioPrevDias }))}
              />
            </label>
            <label className="mb-0 d-flex align-items-center" style={{ gap: 6 }}>
              Duração
              <CampoDia
                ariaLabel="Dias de duração da macroatividade"
                value={macro.diasParaExecutar}
                onChange={(diasParaExecutar) => onAlterar((atual) => atualizarMacroatividade(atual, macro.rowKey, { diasParaExecutar }))}
              />
            </label>
            <span>{fim == null ? 'Fim dia —' : `Fim dia ${fim}`}</span>
            <strong>{currencyFormatter.format(custoDaMacro(estado, macro.rowKey, propItens))}</strong>
            <IconButton size="small" aria-label={aberto ? 'Recolher macroatividade' : 'Expandir macroatividade'} onClick={() => setAberto((atual) => !atual)}>
              {aberto ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <IconButton size="small" aria-label="Excluir macroatividade" onClick={() => onAlterar((atual) => excluirMacroatividade(atual, macro.rowKey))}>
              <Delete fontSize="small" />
            </IconButton>
          </div>
        </div>
        {aberto && (
          <div className="mt-3">
            <Nav tabs>
              <NavItem>
                <NavLink href="#" className={aba === 'atividades' ? 'active' : ''} onClick={(event) => { event.preventDefault(); setAba('atividades'); }}>
                  Atividades
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink href="#" className={aba === 'itens' ? 'active' : ''} onClick={(event) => { event.preventDefault(); setAba('itens'); }}>
                  Itens orçamentários
                </NavLink>
              </NavItem>
            </Nav>
            {aba === 'atividades' && (
              <div className="mt-3">
                <div className="d-flex justify-content-end mb-2">
                  <Button size="sm" color="primary" onClick={() => setModalAberto(true)}>Adicionar atividade</Button>
                </div>
                <TabelaAtividades
                  data={linhasAtividade(atividades, estado, propItens, estado.macroatividades)}
                  onAlterar={onAlterar}
                  onAbrir={onAbrirAtividade}
                  rotuloPagina="Atividades por página"
                />
              </div>
            )}
            {aba === 'itens' && (
              <div className="mt-3">
                <MaterialReactTable
                  {...propsTabela}
                  columns={colunasItem}
                  data={itens}
                  muiTablePaginationProps={{
                    labelRowsPerPage: <span style={{ fontSize: '12px' }}>Itens por página</span>,
                    rowsPerPageOptions: [10, 50, 100],
                  }}
                />
              </div>
            )}
          </div>
        )}
      </CardBody>
      {modalAberto && (
        <ModalIncluirAtividades
          macro={macro}
          estado={estado}
          propItens={propItens}
          onFechar={() => setModalAberto(false)}
          onAlterar={onAlterar}
        />
      )}
    </Card>
  );
}

export default function AtividadesOrcamentoPainel({ propItens, estado, setores, cargoSetorAtiv, onAlterar }) {
  const [aba, setAba] = useState('macros');
  const [atividadeAberta, setAtividadeAberta] = useState(null);
  const linhas = linhasAtividade(estado.atividades || [], estado, propItens, estado.macroatividades || []);

  return (
    <div className="orcamento-content-panel mt-3">
      <Nav tabs>
        <NavItem>
          <NavLink href="#" className={aba === 'macros' ? 'active' : ''} onClick={(event) => { event.preventDefault(); setAba('macros'); }}>
            Macroatividades
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink href="#" className={aba === 'atividades' ? 'active' : ''} onClick={(event) => { event.preventDefault(); setAba('atividades'); }}>
            Atividades
          </NavLink>
        </NavItem>
      </Nav>

      {aba === 'macros' && (
        <div className="mt-3">
          <div className="d-flex justify-content-end mb-3">
            <Button size="sm" color="primary" onClick={() => onAlterar((atual) => adicionarMacroatividade(atual))}>
              Nova macroatividade
            </Button>
          </div>
          {(estado.macroatividades || []).map((macro) => (
            <CardMacroatividade
              key={macro.rowKey}
              macro={macro}
              estado={estado}
              propItens={propItens}
              onAlterar={onAlterar}
              onAbrirAtividade={setAtividadeAberta}
            />
          ))}
          {!estado.macroatividades?.length && <p>Nenhuma macroatividade nesta proposta.</p>}
        </div>
      )}

      {aba === 'atividades' && (
        <div className="mt-3">
          <div className="d-flex justify-content-end mb-2">
            <Button size="sm" color="primary" onClick={() => onAlterar((atual) => adicionarAtividade(atual, null))}>
              Nova atividade
            </Button>
          </div>
          <TabelaAtividades
            data={linhas}
            comMacro
            onAlterar={onAlterar}
            onAbrir={setAtividadeAberta}
            onExcluir={(rowKey) => onAlterar((atual) => excluirAtividade(atual, rowKey))}
            rotuloPagina="Atividades por página"
          />
        </div>
      )}

      <ModalDetalheAtividade
        atividade={atividadeAberta}
        estado={estado}
        propItens={propItens}
        setores={setores}
        cargoSetorAtiv={cargoSetorAtiv}
        onFechar={() => setAtividadeAberta(null)}
        onAlterar={onAlterar}
      />
    </div>
  );
}
